import { c as _c } from "react/compiler-runtime";
import { usePageAction } from '@cc-shopping/platform-analytics/hooks/usePageAction';
import { CdnImage } from '@cc-shopping/ui/primitives/CdnImage';
import { debounce } from '@cc-shopping/utils/function/debounce';
import { Autocomplete, Box, type BoxProps, ButtonBase, ListItem, ListItemAvatar, type ListItemProps, ListItemText, type ListProps, TextField, type TextFieldProps } from '@mui/material';
import fuzzysort from 'fuzzysort';
import { type ChangeEventHandler, forwardRef, type HTMLAttributes, type Key, startTransition, useCallback, useEffect, useState } from 'react';
import { useCompareContext } from '../../../Context';
import type { MarketplaceIds } from '../../../Context/types';
import { ListContainer } from './ListContainer';
export type AutoCompleteRow = {
  id: string;
  label: string;
  imgSource: string;
};
type SearchAutoCompleteProps = {
  dataset: AutoCompleteRow[];
  maxLength?: number;
  containerProps?: BoxProps;
  textFieldProps?: TextFieldProps;
  listProps?: ListProps;
};
const RenderOption = t0 => {
  const $ = _c(23);
  let idsToCompare;
  let option;
  let props;
  if ($[0] !== t0) {
    ({
      option,
      idsToCompare,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = idsToCompare;
    $[2] = option;
    $[3] = props;
  } else {
    idsToCompare = $[1];
    option = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== idsToCompare || $[5] !== option.id) {
    t1 = idsToCompare.includes(option.id) && {
      opacity: 0.25,
      pointerEvents: "none"
    };
    $[4] = idsToCompare;
    $[5] = option.id;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = {
      textAlign: "left",
      flex: 1,
      padding: 1.5,
      ...t1
    };
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      marginRight: 3,
      alignItems: "center",
      display: "flex",
      position: "relative"
    };
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundSize: "contain"
    };
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== option.imgSource || $[12] !== option.label) {
    t5 = <ListItemAvatar sx={t3}><CdnImage alt={option.label} src={option.imgSource} width={68} height={37.5} style={t4} /></ListItemAvatar>;
    $[11] = option.imgSource;
    $[12] = option.label;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      alignSelf: "center"
    };
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== option.label) {
    t7 = <ListItemText primary={option.label} sx={t6} />;
    $[15] = option.label;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== option.id || $[18] !== props || $[19] !== t2 || $[20] !== t5 || $[21] !== t7) {
    t8 = <ListItem alignItems="flex-start" disableGutters={true} sx={t2} divider={true} {...props} id={option.id}>{t5}{t7}</ListItem>;
    $[17] = option.id;
    $[18] = props;
    $[19] = t2;
    $[20] = t5;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
};
export const SearchAutoComplete = forwardRef<HTMLDivElement, SearchAutoCompleteProps>(({
  dataset,
  maxLength = 10,
  containerProps,
  textFieldProps
}, ref) => {
  const [controlledSearchValue, setControlledSearchValue] = useState('');
  const [filteredDataset, setFilteredDataset] = useState<AutoCompleteRow[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [typing, setTyping] = useState<boolean>(false);
  const {
    areSlotsOpen,
    idsToCompare,
    isLoading,
    addIdToCompare
  } = useCompareContext();
  const cardOnClickFirePageAction = usePageAction({
    actionName: 'cc_add_to_compare_card',
    actionSection: 'credit_card_search',
    actionType: 'select'
  });
  const filterValuesUsingDebounce = useCallback(debounce((value: string) => {
    if (value) {
      const results = fuzzysort.go<AutoCompleteRow>(value, dataset, {
        key: 'label',
        limit: maxLength
      });
      const rows = [...results].map(row => row.obj);
      setFilteredDataset(rows);
    } else {
      setFilteredDataset([]);
    }
    setTyping(false);
  }, 500), [dataset]);
  useEffect(() => {
    setTyping(true);
    filterValuesUsingDebounce(controlledSearchValue);
  }, [controlledSearchValue]);
  const handleSearchTextChanged: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = ({
    currentTarget: {
      value: value_0
    }
  }) => {
    setControlledSearchValue(value_0);
    startTransition(() => {
      setOpen(!!value_0);
    });
  };
  const handleClick = (option: AutoCompleteRow) => {
    const selectedOptionIndex = filteredDataset.findIndex(card => card.id === option.id);
    cardOnClickFirePageAction({
      action_input: option.id,
      additional_attributes: {
        card_position: selectedOptionIndex
      }
    });
    startTransition(() => {
      addIdToCompare(option.id);
      resetSearch();
    });
  };
  const resetSearch = () => {
    setControlledSearchValue('');
    setFilteredDataset([]);
    setOpen(false);
  };
  const renderOptionCallback = useCallback((props: HTMLAttributes<HTMLLIElement> & {
    key: Key;
  }, option_0: AutoCompleteRow) => {
    const {
      key,
      ...rest
    } = props;
    return <RenderOption option={option_0} idsToCompare={idsToCompare} key={key} {...rest} />;
  }, [idsToCompare]);
  return <Box className="search-bar" sx={{
    width: '344px'
  }} textAlign="center" position="relative" ref={ref} {...containerProps}>
        <Autocomplete value={null} filterOptions={options => options} loading={typing} open={open} autoFocus onClose={resetSearch} onChange={(_, newValue) => {
      if (newValue) {
        handleClick(newValue);
      }
    }} clearOnBlur clearOnEscape openOnFocus blurOnSelect options={filteredDataset} getOptionLabel={option_1 => option_1.label} getOptionKey={option_2 => option_2.id} PaperComponent={ListContainer} disabled={isLoading || !areSlotsOpen} renderOption={renderOptionCallback} renderInput={params => <TextField {...params} label="Search card name" variant="outlined" value={controlledSearchValue} onChange={handleSearchTextChanged} slotProps={{
      input: {
        ...params.InputProps,
        sx: {
          pr: '9px !important'
        },
        endAdornment: controlledSearchValue ? <ButtonBase onClick={resetSearch}>
                      <span aria-hidden="true" className="mask-icon mask-icon-close" style={{
            width: '24px',
            height: '24px'
          }} />
                    </ButtonBase> : <span aria-hidden="true" data-testid="search-icon" className="mask-icon mask-icon-search" style={{
          width: '24px',
          height: '24px'
        }} />
      },
      htmlInput: {
        ...params.inputProps,
        value: controlledSearchValue
      }
    }} sx={{
      width: '100%',
      pr: 0
    }} {...textFieldProps} />} />
      </Box>;
});